import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";
import { Ai as sdkAi } from "coolremote-sdk";

// coolremote-sdk's Ai methods are untyped (return Promise<any>) — these describe just the
// fields read below, from the response shapes the backend team documented on CM-419.
type ChatHistoryRow = { id: string; prompt: string; suggested_response: string };
type ChatSession = {
  chatId: string;
  name: string;
  token: string;
  lastMessageAt?: string | null;
  lastMessagePreview?: string | null;
  // The chat's effective site scope; empty/null means every site.
  siteIds?: string[] | null;
};

/**
 * Sends chat prompts to the CoolAssistant AI assistant for a given customer, over a
 * session-token-scoped conversation. A session is resolved lazily (on the first
 * `getChatHistory`/`handleSend` call): the customer's most recent existing chat is resumed if
 * one exists, otherwise a new one is created. Dropped whenever the customer changes, or
 * explicitly replaced via `handleNewChat`/`handleSelectChat`.
 *
 * `getRecentChats`/`handleNewChat`/`handleSelectChat` add multi-chat support on top of that:
 * `getRecentChats` lists the customer's last chats (backend-ordered, newest first — sliced to
 * 5 here), `handleSelectChat` switches the active session to a chat picked from that list, and
 * `handleNewChat` eagerly creates a fresh chat and makes it the active session.
 *
 * `selectedSiteIds`/`handleSelectedSiteIdsChange` drive the widget's site-scope chip, and the
 * selection is sent with every message (`siteIds`, or `siteId: "all"` when empty). The site
 * selected in the app (e.g. the dashboard filter) is the default scope: a new chat starts on it,
 * the auto-resumed chat is opened on it, and the chip follows it whenever it changes. With no
 * site selected in the app, the auto-resumed chat restores the scope the BE remembered for it;
 * a chat explicitly picked from Recents always restores its remembered scope. Whatever scope the
 * BE reports back on each reply replaces the selection, so sites the actor lost access to drop
 * out of the chip rather than being re-sent.
 * @param customerId - The customer the AI conversation runs against.
 * @param siteId - The site selected in the app, if any — the chip's default site scope.
 * @param userId - The current user, passed through to scope the session.
 */
const useCoolAssistant = (customerId?: string | null, siteId?: string | null, userId?: string | null) => {
  // Keyed by the customer it was created for — validated synchronously inside `ensureSession`
  // on every call, rather than invalidated via a separate effect keyed on [customerId]. An
  // effect-based invalidation raced with CoolAssistantWidget's own history-reload effect (child
  // effects run before parent effects), so a stale token could still look valid — and get used
  // to fetch the previous customer's history — for one render after the customer actually
  // changed.
  const sessionRef = useRef<{ token: string; chatId?: string; customerId?: string | null } | null>(null);
  // Cached from the last getRecentChats() call, keyed by chatId — onSelectChat needs each
  // chat's token (listChatSessions returns it, but onSelectChat is only ever given a chatId).
  const recentChatsRef = useRef<Record<string, { token: string; siteIds?: string[] | null }>>({});
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  // Mirrors selectedSiteIds so handleSend reads the selection ensureSession just set, before
  // the state update lands. Keyed by customer so a selection made under one customer is never
  // sent into another's chat.
  const selectionRef = useRef<{ siteIds: string[]; customerId?: string | null }>({ siteIds: [] });

  const siteIdRef = useRef(siteId);
  siteIdRef.current = siteId;

  const applySelection = useCallback((siteIds: string[]) => {
    selectionRef.current = { siteIds, customerId };
    setSelectedSiteIds(siteIds);
  }, [customerId]);

  const getAppSiteIds = () => (siteIdRef.current ? [siteIdRef.current] : []);

  // Keep the chip in sync with the site selected in the app
  useEffect(() => {
    applySelection(siteId ? [siteId] : []);
  }, [siteId, applySelection]);

  // Resumes the customer's most recent existing chat rather than always creating a new one —
  // creating unconditionally meant every widget mount with no cached session (a page reload, a
  // customer switch) spun up a permanent empty "New chat" the user never asked for, since
  // getChatHistory needs a token before it can even ask "is there history?".
  const ensureSession = useCallback(async () => {
    const current = sessionRef.current;
    if (current && current.customerId === customerId) {
      return current;
    }
    const { sessions } = await sdkAi.listChatSessions({ customerId });
    const mostRecent: ChatSession | undefined = sessions?.[0];
    const { token, chatId } = mostRecent ?? (await sdkAi.createChatSession({ customerId, siteId: siteIdRef.current, userId }));
    const session = { token, chatId, customerId };
    sessionRef.current = session;
    const appSiteIds = getAppSiteIds();
    applySelection(appSiteIds.length || !mostRecent ? appSiteIds : mostRecent.siteIds ?? []);
    return session;
  }, [customerId, userId, applySelection]);

  // Disabled for now — plain text only (see handleSend/getChatHistory below). Re-enable by
  // swapping the return of handleSend/getChatHistory back to routing through this and
  // including `suggested_response_html`/`files` on each item.
  // const resolveExportLinks = useCallback(async (res: any) => {
  //   if (!res?.files?.length) {
  //     return res;
  //   }
  //   const files = await Promise.all(
  //     res.files.map(async (file: any) => {
  //       if (!file?.link) {
  //         return null;
  //       }
  //       try {
  //         const response = await sdkAi.downloadChatFile(file.link);
  //         const blob = await response.blob();
  //         return { ...file, link: URL.createObjectURL(blob) };
  //       } catch (err) {
  //         console.error(`CoolAssistant: failed to download ${file.name ?? file.link}`, err);
  //         return null;
  //       }
  //     }),
  //   );
  //   return { ...res, files: files.filter(Boolean) };
  // }, []);

  // Plain text only for now — html and files are ignored regardless of what the backend sends.
  const handleSend = useCallback(async (prompt: string) => {
    const { token } = await ensureSession();
    const { siteIds, customerId: selectionCustomerId } = selectionRef.current;
    const scopedSiteIds = selectionCustomerId === customerId ? siteIds : [];
    const scope = scopedSiteIds.length ? { siteIds: scopedSiteIds } : { siteId: "all" };
    const res = await sdkAi.sendChatMessage(token, prompt, scope);
    if (Array.isArray(res?.siteIds)) {
      applySelection(res.siteIds);
    }
    return { text: res.suggested_response };
    // return resolveExportLinks(res);
  }, [ensureSession, customerId, applySelection]);

  const getChatHistory = useCallback(async () => {
    const { chatId } = await ensureSession();
    if (!chatId) {
      return [];
    }
    const res = await sdkAi.getChatHistoryByChatId(chatId);
    // const turns = await Promise.all((res ?? []).map((item: any) => resolveExportLinks(item)));

    // History comes back newest-first; flatten each turn into its user/assistant pair.
    return [...(res ?? [])].reverse().flatMap((item: ChatHistoryRow) => [
      { id: `${item.id}_u`, role: "user" as const, text: item.prompt },
      { id: `${item.id}_a`, role: "assistant" as const, text: item.suggested_response },
      // {
      //   id: `${item.id}_a`,
      //   role: "assistant" as const,
      //   suggested_response: item.suggested_response,
      //   suggested_response_html: item.suggested_response_html,
      //   files: item.files,
      // },
    ]);
  }, [ensureSession]);

  // listChatSessions must be called *without* a token to get the list — passing one narrows
  // the result to that single chat instead.
  //
  // Chats with no messages are dropped before the slice, not after: they'd otherwise render as
  // bare "New chat" rows (QA on CM-419), and slicing first would let them eat slots out of the
  // five real ones. `lastMessageAt` is the documented "no messages" signal — deliberately not
  // `turnCount`, which counts the *live* session only and reads 0 for a real chat whose ~2h
  // session has expired, so filtering on it would hide genuine conversations.
  const getRecentChats = useCallback(async () => {
    const res = await sdkAi.listChatSessions({ customerId });
    const sessions: ChatSession[] = (res?.sessions ?? [])
      .filter((s: ChatSession) => Boolean(s.lastMessageAt))
      .slice(0, 5);
    recentChatsRef.current = Object.fromEntries(
      sessions.map((s) => [s.chatId, { token: s.token, siteIds: s.siteIds }]),
    );
    return sessions.map((s) => ({
      id: s.chatId,
      title: s.name,
      preview: s.lastMessagePreview ?? undefined,
    }));
  }, [customerId]);

  // Creates the new chat eagerly (rather than just clearing sessionRef and letting the next
  // ensureSession() lazily resolve one) — ensureSession now resumes the most recent existing
  // chat when nothing is cached, which would just reopen the chat the user is trying to leave.
  const handleNewChat = useCallback(async () => {
    const { token, chatId } = await sdkAi.createChatSession({ customerId, siteId: siteIdRef.current, userId });
    sessionRef.current = { token, chatId, customerId };
    applySelection(getAppSiteIds());
  }, [customerId, userId, applySelection]);

  const handleSelectChat = useCallback(async (chatId: string) => {
    const cached = recentChatsRef.current[chatId];
    if (cached) {
      sessionRef.current = { token: cached.token, chatId, customerId };
      applySelection(cached.siteIds ?? []);
    }
    const res = await sdkAi.getChatHistoryByChatId(chatId);

    // Same newest-first-to-user/assistant-pairs flattening as getChatHistory above.
    return [...(res ?? [])].reverse().flatMap((item: ChatHistoryRow) => [
      { id: `${item.id}_u`, role: "user" as const, text: item.prompt },
      { id: `${item.id}_a`, role: "assistant" as const, text: item.suggested_response },
    ]);
  }, [customerId, applySelection]);

  // Disabled for now, along with the back button in CoolAssistantWidget and its `onBack` wiring in
  // CoolAssistant.tsx.
  // const handleBack = useCallback(async () => {
  //   const token = sessionRef.current?.token;
  //   sessionRef.current = null;
  //   if (!token) {
  //     return;
  //   }
  //   try {
  //     await sdkAi.deleteChatSession(token);
  //   } catch (err) {
  //     console.error("CoolAssistant: failed to delete chat session", err);
  //   }
  // }, []);

  // TBD: hardcoded until the backend exposes a suggestions API. Short label for the chip,
  // the full question is what gets sent — generic first, then by expected usage.
  const getDefaultSuggestions = useCallback(() => {
    return [
      { label: t`Commands`, prompt: t`What commands can I use?` },
      { label: t`Open alerts`, prompt: t`Any open critical alerts?` },
      { label: t`What's on`, prompt: t`Which units are on right now?` },
      { label: t`Offline units`, prompt: t`Which units have been disconnected more than 60 minutes?` },
      { label: t`Site summary`, prompt: t`Give me a summary of this site` },
    ];
  }, []);

  return {
    handleSend,
    getChatHistory,
    getRecentChats,
    handleNewChat,
    handleSelectChat,
    /* handleBack, */
    getDefaultSuggestions,
    selectedSiteIds,
    handleSelectedSiteIdsChange: applySelection,
  };
};

export default useCoolAssistant;
